/**
 * Sortable card grid for visio backgrounds.
 */
import * as React from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import type { VisioBackground } from '@/types/visio-background.types';
import { LabelBadge } from '@/components/common/LabelBadge';
import { Button } from '@/components/forms/Button';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { VISIO_BACKGROUND_TOGGLE_CONFIG } from './visio-background-configs';
import { visioBackgroundItemIds } from './visio-background-table-utils';

/** Props for {@link VisioBackgroundCard}. */
interface CardProps {
  background: VisioBackground;
  canEdit: boolean;
  canDelete: boolean;
  onEdit: (id: string) => void;
  onDelete: (background: VisioBackground) => void;
}

/** Sortable visio-background tile with edit and delete actions. */
const VisioBackgroundCard: React.FC<Readonly<CardProps>> = ({
  background,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: background.id,
    disabled: !canEdit,
  });

  function handleDeleteClick(event: React.MouseEvent): void {
    event.stopPropagation();
    onDelete(background);
  }

  function handleEditClick(event: React.MouseEvent): void {
    event.stopPropagation();
    onEdit(background.id);
  }

  const imageClassName = cn(
    'w-full aspect-video object-cover',
    !background.isActive && 'opacity-40 grayscale',
  );

  const cardClassName = cn(
    'rounded-lg border border-border bg-card overflow-hidden flex flex-col',
    isDragging && 'shadow-lg',
  );

  const toggleConfig = background.isActive
    ? VISIO_BACKGROUND_TOGGLE_CONFIG.active
    : VISIO_BACKGROUND_TOGGLE_CONFIG.inactive;

  function renderDragHandle(): React.ReactNode {
    if (!canEdit) return null;
    return (
      <button
        type="button"
        className="absolute top-2 left-2 rounded-md bg-black/50 p-1 text-white cursor-grab active:cursor-grabbing touch-none"
        aria-label="Déplacer"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="w-4 h-4" />
      </button>
    );
  }

  function renderDeleteButton(): React.ReactNode {
    if (!canDelete) return null;
    return (
      <Button
        preset="row-delete"
        aria-label={`Supprimer ${background.name}`}
        onClick={handleDeleteClick}
      />
    );
  }

  function renderEditButton(): React.ReactNode {
    if (!canEdit) return null;
    return (
      <Button
        preset="row-edit"
        aria-label={`Modifier ${background.name}`}
        onClick={handleEditClick}
      />
    );
  }

  function renderImageActions(): React.ReactNode {
    if (!canEdit && !canDelete) return null;
    return (
      <div className="absolute top-2 right-2 z-10 flex items-center gap-1">
        {renderEditButton()}
        {renderDeleteButton()}
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      className={cardClassName}
    >
      <div className="relative">
        <img src={background.imageUrl} alt={background.name} className={imageClassName} />
        {renderDragHandle()}
        {renderImageActions()}
      </div>
      <div className="flex items-center gap-2 p-2">
        <p className="flex-1 min-w-0 truncate text-sm font-medium text-foreground">
          {background.name}
        </p>
        <LabelBadge
          label={toggleConfig.label}
          icon={toggleConfig.icon}
          variant={toggleConfig.badgeVariant}
          color={toggleConfig.badgeColor}
          size="sm"
          className="min-w-0 shrink-0"
        />
      </div>
    </div>
  );
};

/** Props for {@link VisioBackgroundsGrid}. */
interface VisioBackgroundsGridProps extends Omit<CardProps, 'background'> {
  backgrounds: VisioBackground[];
  isLoading?: boolean;
  onReorder: (orderedIds: string[]) => void;
}

/** Sortable card grid for visio backgrounds. */
export const VisioBackgroundsGrid: React.FC<Readonly<VisioBackgroundsGridProps>> = ({
  backgrounds,
  isLoading = false,
  onReorder,
  ...cardProps
}) => {
  const [items, setItems] = React.useState(backgrounds);

  React.useEffect(() => setItems(backgrounds), [backgrounds]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((b) => b.id === active.id);
    const newIndex = items.findIndex((b) => b.id === over.id);
    const reordered = arrayMove(items, oldIndex, newIndex);
    setItems(reordered);
    onReorder(reordered.map((b) => b.id));
  };

  const itemIds = visioBackgroundItemIds(items);

  function renderBackgroundCard(background: VisioBackground): React.ReactNode {
    return <VisioBackgroundCard key={background.id} background={background} {...cardProps} />;
  }

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Spinner size="lg" label="Chargement..." />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-12 text-center">
        Aucun arrière-plan pour le moment.
      </p>
    );
  }

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <SortableContext items={itemIds} strategy={rectSortingStrategy}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {items.map(renderBackgroundCard)}
        </div>
      </SortableContext>
    </DndContext>
  );
};
